import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageShell, SectionLabel } from "@/components/Layout";
import { useAuth, loginWithGoogle } from "@/lib/auth";
import { toast } from "sonner";
import { Calendar, Clock, ArrowRight, Bell, Sparkles } from "lucide-react";
import { DISCIPLINES } from "@/lib/disciplines";
import Seo from "@/components/Seo";
import { trackEvent } from "@/lib/analytics";

// City picker: canonical India cities + two escape hatches with free-text follow-ups.
const CITIES = [
  "mumbai", "delhi", "bangalore", "chennai", "kolkata",
  "hyderabad", "ahmedabad", "kochi", "pune", "jaipur", "goa",
  "other_india", "outside_india",
];
const CITY_LABEL = {
  other_india: "Other (India)",
  outside_india: "Outside India",
};
// Disciplines beyond the core 9 that we accept at booking time (not stored in
// the global palette map; captured as free-text equivalents on the profile).
const EXTRA_DISCIPLINES = [
  { key: "manager_agent", label: "Manager / Agent" },
  { key: "other", label: "Other" },
];

// International phone — accepts +country code, spaces, dashes, parens, 7–20 chars.
const PHONE_RE = /^[0-9+\-\s()]{7,20}$/;

// FAQ copy — rendered in the UI AND emitted as application/ld+json schema.
const FAQ = [
  {
    q: "How does a session work?",
    a: "Thirty focused minutes, 1-on-1, on Google Meet. You'll get a calendar invite with the link right after booking. Come prepared with the one thing you want clarity on — the advisors are briefed to go deep, not sell you anything.",
  },
  {
    q: "What does a session cost?",
    a: "Rates are set by each advisor and start at ₹2,500. All prices on the roster are exclusive of 18% GST — the full breakdown is shown before you pay. Payment is captured upfront via Razorpay.",
  },
  {
    q: "Can I reschedule or cancel?",
    a: "You get one free reschedule per booking from your dashboard, up to 24 hours before the session. Cancellations free the slot but fees are non-refundable — see our Refunds policy for the full detail.",
  },
  {
    q: "I'm outside India. Can I still book?",
    a: "Yes. Slot times are shown in IST and in your local timezone side-by-side. Payment is in INR; Razorpay accepts international cards. If your bank blocks it, email hello@anartizen.com and we'll help you around it.",
  },
  {
    q: "What if my favourite advisor has no open slots?",
    a: "You'll see an OPENING SOON badge on their card — leave your email and we'll ping you the moment they open their calendar. No spam, one email, you can delete the alert any time.",
  },
  {
    q: "Who sees what I write in the intake questions?",
    a: "Only the advisor you booked, and us (so we can prep the session). We never share your writing with other members or publish it anywhere.",
  },
  {
    q: "Is this subscription-based?",
    a: "No. Each session is a one-off purchase. There's no membership gate — book one, book ten, or never come back. We'd rather earn each session.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

// Local-TZ helper — formats the slot's local time next to IST so international
// visitors don't have to do mental math.
function localTzLabel(iso) {
  try {
    const d = new Date(iso);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "local";
    // Short name like "EDT" / "GMT+5:30"
    const parts = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(d);
    const name = parts.find((p) => p.type === "timeZoneName")?.value || tz;
    const hhmm = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${hhmm} ${name}`;
  } catch (_) { return ""; }
}

// Lazy-load Razorpay only when the user is actually about to pay. Keeps the
// ~90KB script off every non-checkout surface.
function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window.Razorpay !== "undefined") { resolve(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
}

// ============= NOTIFY ME · lead capture when advisor has no open slots =============
function NotifyMeForm({ advisor }) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
    if (user?.name && !name) setName(user.name);
  }, [user, email, name]);

  const submit = async (e) => {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      toast.error("Enter a valid email so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/advisors/${advisor.slug}/notify`, { email: em, name: name.trim() });
      setDone(true);
      trackEvent("ua_notify_me_submitted", { advisor_slug: advisor.slug });
      toast.success(`On the list for ${advisor.name.split(" ")[0]}'s next slots.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save your request. Try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid={`notify-done-${advisor.slug}`} className="border border-secondary/40 bg-secondary/[0.06] p-5">
        <p className="meta-mono !text-secondary mb-2">— YOU&apos;RE ON THE LIST</p>
        <p className="text-sm text-white/80 leading-relaxed">
          We&apos;ll email <strong>{email}</strong> the moment {advisor.name.split(" ")[0]} opens new slots.
        </p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} data-testid={`notify-form-${advisor.slug}`} className="border border-border p-5 mt-4">
      <p className="meta-mono !text-primary mb-2 flex items-center gap-2"><Bell size={11} /> NOTIFY ME</p>
      <p className="text-sm text-white/70 leading-relaxed mb-4">
        {advisor.name.split(" ")[0]}&apos;s calendar is full right now. Drop your email and we&apos;ll ping you the moment new slots open — no spam, no newsletter sign-up.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
        <input
          data-testid={`notify-name-${advisor.slug}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          maxLength={120}
          className="bg-transparent outline-none text-sm border border-border focus:border-primary px-3 py-2"
        />
        <input
          data-testid={`notify-email-${advisor.slug}`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@domain.com"
          maxLength={200}
          className="bg-transparent outline-none text-sm border border-border focus:border-primary px-3 py-2"
        />
        <button
          type="submit"
          disabled={submitting}
          data-testid={`notify-submit-${advisor.slug}`}
          className="btn-pill btn-coral justify-center disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? "Saving…" : <>Notify me <ArrowRight size={13} /></>}
        </button>
      </div>
    </form>
  );
}

// ============= ADVISOR CARD =============
function AdvisorCard({ a, selected, onSelect }) {
  const openSoon = a.has_slots === false;
  return (
    <div
      data-testid={`advisor-${a.slug}`}
      role="button"
      tabIndex={0}
      aria-label={openSoon ? `${a.name} — opening soon, join the notify list` : `${a.name} — book a session`}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      className={`bg-background p-6 text-left transition flex gap-6 items-start cursor-pointer ${selected ? "ring-1 ring-primary bg-white/[0.02]" : "hover:bg-white/[0.02]"}`}
    >
      <div className="w-32 h-32 md:w-40 md:h-40 shrink-0 bg-muted overflow-hidden">
        {(a.image || a.image_url) && (
          <img src={a.image || a.image_url} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="meta-mono !text-primary">{a.expertise || a.role}</p>
        <h3 className="font-display text-2xl md:text-3xl font-bold mt-1 leading-tight">{a.name}</h3>
        <p className="text-white/65 text-sm mt-3 leading-relaxed">{a.bio}</p>
        {a.rate_inr > 0 && (
          <p className="meta-mono !text-white/55 mt-4" data-testid={`advisor-price-${a.slug}`}>
            — ₹{a.rate_inr.toLocaleString("en-IN")}<span className="text-primary">*</span> / SESSION
          </p>
        )}
        {openSoon ? (
          <span
            data-testid={`advisor-opening-soon-${a.slug}`}
            aria-label={`${a.name} — opening soon`}
            className="mt-5 inline-flex items-center gap-2 bg-white/[0.06] text-white/70 border border-white/15 font-mono text-xs tracking-[0.18em] uppercase font-semibold px-5 py-2.5 rounded-full"
          >
            <Bell size={13} /> Opening soon
          </span>
        ) : (
          <button
            type="button"
            data-testid={`advisor-book-${a.slug}`}
            aria-label={`Book a session with ${a.name}`}
            onClick={(e) => { e.stopPropagation(); onSelect(); }}
            className="mt-5 inline-flex items-center gap-2 bg-white text-black font-mono text-xs tracking-[0.18em] uppercase font-semibold px-5 py-2.5 rounded-full hover:bg-white/90 active:bg-white/80 transition"
          >
            Book <ArrowRight size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

function AdvisorSkeleton() {
  return (
    <div className="bg-background p-6 flex gap-6 items-start animate-pulse" data-testid="advisor-skeleton">
      <div className="w-32 h-32 md:w-40 md:h-40 shrink-0 bg-white/[0.04]" />
      <div className="flex-1 space-y-3">
        <div className="h-3 w-28 bg-white/[0.06]" />
        <div className="h-6 w-56 bg-white/[0.08]" />
        <div className="h-3 w-full bg-white/[0.04]" />
        <div className="h-3 w-4/5 bg-white/[0.04]" />
        <div className="h-8 w-24 bg-white/[0.06] rounded-full mt-3" />
      </div>
    </div>
  );
}

// Fallback used ONLY if `/api/ua/weekly-questions` doesn't respond. The server
// returns a weekly-rotated slice from a 20-question bank so this array stays
// dormant in practice.
const UA_QUESTIONS_FALLBACK = [
  { q: "How do I release my first single on streaming?",    tag: "music" },
  { q: "What do I charge for a 30-second brand film?",      tag: "film" },
  { q: "Should I quit my day job for my art?",              tag: "career" },
];

const TAG_COLOR = {
  music:    "text-primary",
  career:   "text-secondary",
  brand:    "text-primary",
  film:     "text-secondary",
  audience: "text-primary",
  craft:    "text-secondary",
  writing:  "text-primary",
};

// ============= HERO PREVIEW · weekly-rotated advisor-question row =============
function QuestionRow({ q, tag }) {
  return (
    <div
      data-testid={`question-row-${tag}`}
      className="flex items-start gap-3 md:gap-4 px-4 md:px-6 py-4 min-w-0"
    >
      <span className="font-display text-2xl md:text-3xl leading-none text-primary/70 shrink-0 translate-y-1">&ldquo;</span>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-sm md:text-[0.95rem] leading-snug text-white/90">
          {q}
        </p>
        <p className={`meta-mono mt-2 ${TAG_COLOR[tag] || "!text-white/55"}`}>
          — {(tag || "ua").toUpperCase()}
        </p>
      </div>
    </div>
  );
}


export default function UnfairAdvantage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const advisorParam = searchParams.get("advisor");
  const [advisors, setAdvisors] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [slotData, setSlotData] = useState({ slots: [], configured: true, loading: false });
  const [slot, setSlot] = useState(null);
  const [whyAdvisor, setWhyAdvisor] = useState("");
  const [whatToDiscuss, setWhatToDiscuss] = useState("");
  // Artist-profile fields captured during booking → populate the Artizen ID
  const [bookName, setBookName] = useState("");
  const [bookPhone, setBookPhone] = useState("");
  const [bookDiscipline, setBookDiscipline] = useState("");
  const [bookDisciplineOther, setBookDisciplineOther] = useState("");
  const [bookCity, setBookCity] = useState("");
  const [bookCityOther, setBookCityOther] = useState("");
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);
  // Coupon code — user-entered at checkout. We validate against /api/promo/validate
  // and surface a confirmed discount; the server re-validates on /bookings/order so
  // the client can't forge discounts. appliedPromo is {code, discount_pct} once valid.
  const [couponInput, setCouponInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [couponStatus, setCouponStatus] = useState({ loading: false, error: "" });
  // Weekly-rotated questions in the hero rail — same slice all week, rolls
  // to the next slice automatically every Monday via the ISO-week math on the
  // backend. No cron / admin edit needed to refresh.
  const [weeklyQuestions, setWeeklyQuestions] = useState(UA_QUESTIONS_FALLBACK);
  useEffect(() => {
    api.get("/ua/weekly-questions?limit=3")
      .then(({ data }) => { if (Array.isArray(data?.questions) && data.questions.length) setWeeklyQuestions(data.questions); })
      .catch(() => { /* fallback stays */ });
  }, []);

  // Prefill Name from Google profile once user is logged in (unless the user has already typed something)
  useEffect(() => {
    if (user?.name && !bookName) setBookName(user.name);
  }, [user, bookName]);

  useEffect(() => {
    setRosterLoading(true);
    api.get("/advisors").then(({ data }) => {
      setAdvisors(data.advisors);
      setRosterLoading(false);
      // Attempt to resume a pre-login booking draft (advisor + slot + intake)
      let resumeSlug = null;
      let draft = null;
      try {
        const raw = window.sessionStorage.getItem("ua_pending_booking");
        if (raw) {
          draft = JSON.parse(raw);
          window.sessionStorage.removeItem("ua_pending_booking");
          resumeSlug = draft?.advisor_slug || null;
        }
      } catch (_e) { /* ignore */ }
      const slug = resumeSlug || advisorParam;
      if (slug) {
        const a = data.advisors.find((x) => x.slug === slug);
        if (a) {
          setSelected(a);
          if (draft) {
            if (draft.slot) setSlot(draft.slot);
            if (draft.whyAdvisor) setWhyAdvisor(draft.whyAdvisor);
            if (draft.whatToDiscuss) setWhatToDiscuss(draft.whatToDiscuss);
            if (draft.bookName) setBookName(draft.bookName);
            if (draft.bookPhone) setBookPhone(draft.bookPhone);
            if (draft.bookDiscipline) setBookDiscipline(draft.bookDiscipline);
            if (draft.bookDisciplineOther) setBookDisciplineOther(draft.bookDisciplineOther);
            if (draft.bookCity) setBookCity(draft.bookCity);
            if (draft.bookCityOther) setBookCityOther(draft.bookCityOther);
            setReviewMode(true);
          }
          // Wait for the actual card to render in the DOM before scrolling.
          // On slow mobile connections the previous fixed 250ms timeout was racing
          // the roster render and landing on the hero. We retry up to ~2s.
          let tries = 0;
          const t = setInterval(() => {
            tries += 1;
            const card = document.querySelector(`[data-testid="advisor-${a.slug}"]`);
            const panel = document.querySelector('[data-testid="ua-booking-panel"]');
            if (card || panel) {
              (card || panel).scrollIntoView({ behavior: "smooth", block: "start" });
              clearInterval(t);
            } else if (tries > 20) {
              clearInterval(t);
            }
          }, 100);
        }
      }
    }).catch(() => setRosterLoading(false));
  }, [advisorParam]);

  const prevSlugRef = React.useRef(null);
  useEffect(() => {
    if (!selected) { setSlotData({ slots: [], configured: true, loading: false }); setSlot(null); prevSlugRef.current = null; return; }
    setSlotData({ slots: [], configured: true, loading: true });
    // Only reset the slot if the advisor actually changed. This preserves a
    // slot restored from sessionStorage after post-login resume.
    if (prevSlugRef.current && prevSlugRef.current !== selected.slug) setSlot(null);
    prevSlugRef.current = selected.slug;
    api.get(`/advisors/${selected.slug}/slots`)
      .then(({ data }) => setSlotData({ slots: data.slots || [], configured: !!data.configured, loading: false }))
      .catch(() => setSlotData({ slots: [], configured: false, loading: false }));
  }, [selected]);

  const base = selected?.rate_inr || 2500;
  const discountPct = appliedPromo?.discount_pct || 0;
  const discount = Math.round(base * (discountPct / 100));
  const netBase = base - discount;
  const gst = Math.round(netBase * 0.18);
  const total = netBase + gst;

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { setCouponStatus({ loading: false, error: "Enter a coupon code." }); return; }
    setCouponStatus({ loading: true, error: "" });
    try {
      const { data } = await api.post("/promo/validate", { code });
      if (data.valid) {
        setAppliedPromo({ code: data.code, discount_pct: data.discount_pct });
        setCouponInput(data.code);
        setCouponStatus({ loading: false, error: "" });
        trackEvent("ua_coupon_applied", { code: data.code, discount_pct: data.discount_pct });
        toast.success(`${data.code} applied · ${data.discount_pct}% off`);
      } else {
        setAppliedPromo(null);
        setCouponStatus({ loading: false, error: data.message || "That code isn't valid." });
      }
    } catch (e) {
      setAppliedPromo(null);
      setCouponStatus({ loading: false, error: e.response?.data?.detail || "Could not check that code." });
    }
  };

  const removeCoupon = () => {
    setAppliedPromo(null);
    setCouponInput("");
    setCouponStatus({ loading: false, error: "" });
  };

  const resolvedCity = bookCity === "other_india" || bookCity === "outside_india"
    ? bookCityOther.trim().toLowerCase()
    : bookCity;
  const resolvedDiscipline = bookDiscipline === "other" || bookDiscipline === "manager_agent"
    ? (bookDiscipline === "manager_agent" ? "manager_agent" : bookDisciplineOther.trim().toLowerCase())
    : bookDiscipline;

  const book = async () => {
    if (!user) {
      // Persist the full booking draft so we can restore & resume after Google auth
      // (advisor + slot + intake answers + profile fields), and land on a Review & Confirm step.
      try {
        window.sessionStorage.setItem("ua_pending_booking", JSON.stringify({
          advisor_slug: selected?.slug || null,
          slot: slot || null,
          whyAdvisor: whyAdvisor || "",
          whatToDiscuss: whatToDiscuss || "",
          bookName: bookName || "",
          bookPhone: bookPhone || "",
          bookDiscipline: bookDiscipline || "",
          bookDisciplineOther: bookDisciplineOther || "",
          bookCity: bookCity || "",
          bookCityOther: bookCityOther || "",
        }));
      } catch (_e) { /* ignore */ }
      const returnPath = selected ? `/unfair-advantage?advisor=${encodeURIComponent(selected.slug)}` : "/unfair-advantage";
      loginWithGoogle(returnPath); return;
    }
    if (!selected || !slot) { toast.error("Pick an advisor and a slot"); return; }
    if (!bookName.trim()) { toast.error("Please enter your full name"); return; }
    if (!PHONE_RE.test(bookPhone.trim())) { toast.error("Please enter a valid phone number (international formats welcome)"); return; }
    if (!bookDiscipline) { toast.error("Pick your discipline — it lives on your Artizen ID"); return; }
    if (bookDiscipline === "other" && !bookDisciplineOther.trim()) { toast.error("Tell us your discipline in one word."); return; }
    if (!bookCity) { toast.error("Pick your city — it lives on your Artizen ID"); return; }
    if ((bookCity === "other_india" || bookCity === "outside_india") && !bookCityOther.trim()) {
      toast.error("Type your city name so we can place you."); return;
    }
    if (whyAdvisor.trim().length < 10) { toast.error("Tell us why you want to speak with this advisor (min 10 chars)"); return; }
    if (whatToDiscuss.trim().length < 10) { toast.error("Tell us what you want to talk about (min 10 chars)"); return; }
    setReviewMode(false);
    setBooking(true);
    trackEvent("ua_booking_order_started", {
      advisor_slug: selected.slug,
      value_inr: total,
      promo_code: appliedPromo?.code || "",
    });
    try {
      // Step 1: create Razorpay order
      const { data: order } = await api.post("/bookings/order", {
        advisor_slug: selected.slug,
        slot_iso: slot,
        why_this_advisor: whyAdvisor.trim(),
        what_to_discuss: whatToDiscuss.trim(),
        name: bookName.trim(),
        phone: bookPhone.trim(),
        discipline: resolvedDiscipline,
        city: resolvedCity,
        promo_code: appliedPromo?.code || null,
      });

      // Mock path (Razorpay not configured on server): confirm directly
      if (order.mock_mode || !order.razorpay_order_id) {
        const { data } = await api.post("/bookings/verify", { booking_draft_id: order.booking_draft_id });
        setConfirmed({ ...data, advisor_name: selected.name });
        trackEvent("ua_payment_success", { advisor_slug: selected.slug, mock: true, value_inr: total });
        toast.success("Booking confirmed \u2713 (mock mode — set Razorpay keys for real payments)");
        setSelected(null); setSlot(null); setWhyAdvisor(""); setWhatToDiscuss("");
        setBooking(false);
        return;
      }

      // Lazy-load Razorpay checkout.js only when we actually need it.
      const ok = await loadRazorpay();
      if (!ok || typeof window.Razorpay === "undefined") {
        toast.error("Payment library did not load. Refresh and try again.");
        setBooking(false);
        return;
      }
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.razorpay_order_id,
        name: "anArtizen · Unfair Advantage",
        description: `30-min session with ${order.advisor_name}`,
        theme: { color: "#F25C50" },
        prefill: { name: user.name, email: user.email },
        handler: async (resp) => {
          try {
            const { data } = await api.post("/bookings/verify", {
              booking_draft_id: order.booking_draft_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setConfirmed({ ...data, advisor_name: selected.name });
            trackEvent("ua_payment_success", { advisor_slug: selected.slug, value_inr: total, promo_code: appliedPromo?.code || "" });
            toast.success("Booking confirmed \u2713");
            setSelected(null); setSlot(null); setWhyAdvisor(""); setWhatToDiscuss("");
          } catch (e) {
            toast.error(e.response?.data?.detail || "Payment verification failed");
          } finally { setBooking(false); }
        },
        modal: { ondismiss: () => { setBooking(false); toast.message("Checkout closed. Booking not created."); } },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        trackEvent("ua_payment_failed", { advisor_slug: selected.slug, reason: resp.error?.code || "unknown" });
        toast.error(resp.error?.description || "Payment failed");
        setBooking(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start booking");
      setBooking(false);
    }
  };

  // Dynamic SEO: when an advisor is selected via ?advisor=slug, swap title +
  // description so Twitter/WhatsApp/LinkedIn link-cards look personalised.
  const seoTitle = selected?.name
    ? `Book ${selected.name} — Unfair Advantage`
    : "Unfair Advantage — 1-on-1 sessions with India's creative leaders";
  const seoDesc = selected?.bio
    ? `${selected.bio.slice(0, 180)}${selected.bio.length > 180 ? "…" : ""} · 30-min session · ₹${(selected.rate_inr || 2500).toLocaleString("en-IN")} + GST.`
    : "Book one-on-one sessions with the people reshaping India's creative industries. 30 minutes. Zero fluff. Unfair advantage.";

  return (
    <PageShell>
      <Seo
        title={seoTitle}
        description={seoDesc}
        canonical={selected ? `https://anartizen.com/unfair-advantage?advisor=${selected.slug}` : "https://anartizen.com/unfair-advantage"}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }}
      />

      <section className="relative grid-bg border-b border-border overflow-hidden">
        {/* Soft coral glow blob — breaks up the dead space without stealing focus */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full opacity-[0.18] blur-[120px]"
          style={{ background: "radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)" }}
        />
        <div className="relative max-w-[1600px] mx-auto px-5 md:px-10 pt-10 md:pt-14 pb-10 md:pb-14 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          {/* LEFT · title block */}
          <div className="lg:col-span-7 min-w-0 w-full">
            <p className="label-mono mb-4" data-testid="ua-eyebrow">
              <Sparkles size={11} /> UNFAIR ADVANTAGE · 1-ON-1 SESSIONS
            </p>
            <h1 className="font-display font-bold leading-[0.98] md:leading-[0.94] tracking-tight text-[1.6rem] sm:text-[2.25rem] md:text-5xl lg:text-[3.5rem] xl:text-[4.25rem] break-words">
              Every creative deserves an <span className="hl-coral">unfair advantage.</span>
            </h1>
            <p className="mt-6 max-w-xl text-white/80 text-sm md:text-base leading-relaxed">
              Not because success should be easier.<br />
              Because knowledge shouldn&apos;t be exclusive.
            </p>
            <p className="mt-4 max-w-xl text-white/90 text-sm md:text-base leading-relaxed">
              <span className="hl-cyan font-medium">Book one-on-one sessions with the people reshaping India&apos;s creative industries.</span>
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
              <button
                type="button"
                data-testid="ua-hero-book-cta"
                onClick={() => {
                  document.querySelector('[data-testid="ua-roster-anchor"]')
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  trackEvent("ua_hero_cta_clicked", {});
                }}
                className="btn-pill btn-coral text-base px-6 py-3"
              >
                Book a session <ArrowRight size={14} />
              </button>
              <p className="meta-mono !text-white/55">
                — 30 MIN <span className="text-primary">·</span> FROM ₹2,500 <span className="text-primary">·</span> ZERO FLUFF
              </p>
            </div>
          </div>

          {/* RIGHT · weekly-rotated questions members bring to advisors */}
          <aside className="lg:col-span-5 min-w-0 w-full" data-testid="ua-hero-preview">
            <div className="border border-border bg-background/80 backdrop-blur-sm overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-4 border-b border-border">
                <p className="label-mono !text-secondary flex items-center gap-2 min-w-0">
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary" />
                  </span>
                  <span className="truncate">THIS WEEK'S QUESTIONS</span>
                </p>
                <p className="meta-mono !text-white/55 shrink-0">— ANSWERED LIVE</p>
              </div>
              <div className="divide-y divide-border">
                {weeklyQuestions.map((qq, i) => (
                  <QuestionRow key={`${qq.q}-${i}`} q={qq.q} tag={qq.tag} />
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  document.querySelector('[data-testid="ua-roster-anchor"]')
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  trackEvent("ua_hero_cta_clicked", { source: "weekly_questions" });
                }}
                className="w-full text-left px-4 md:px-6 py-4 border-t border-border meta-mono hover:!text-primary transition flex items-center justify-between gap-3 group"
                data-testid="ua-hero-preview-see-all"
              >
                <span className="truncate">— BRING YOURS. BOOK A SESSION</span>
                <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform shrink-0" />
              </button>
            </div>
          </aside>
        </div>
      </section>

      <section data-testid="ua-hero-grid" className="bg-black border-b border-border">
        <div className="max-w-[1600px] mx-auto">
          <img
            src="/ua-hero-grid.png"
            alt="anArtizen presents Unfair Advantage — India's creative ecosystem. The door is now open."
            className="w-full h-auto block select-none"
            draggable={false}
            loading="lazy"
          />
        </div>
      </section>

      <section>
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="meta-mono mb-8" data-testid="ua-roster-anchor">— ROSTER · {rosterLoading ? "LOADING…" : `${advisors.length} ADVISORS`}</h2>
            <div className="grid gap-px bg-border">
              {rosterLoading ? (
                <>
                  <AdvisorSkeleton />
                  <AdvisorSkeleton />
                  <AdvisorSkeleton />
                </>
              ) : advisors.map((a) => {
                const openSoon = a.has_slots === false;
                const pick = () => {
                  setSelected(a);
                  trackEvent("ua_advisor_selected", { advisor_slug: a.slug, has_slots: !!a.has_slots });
                  // On mobile the booking panel sits below the advisor list. Scroll
                  // to it so the slots appear where the user's attention is.
                  if (window.matchMedia("(max-width: 1023px)").matches) {
                    setTimeout(() => {
                      document.querySelector('[data-testid="ua-booking-panel"]')
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 80);
                  }
                };
                return (
                  <React.Fragment key={a.slug}>
                    <AdvisorCard a={a} selected={selected?.slug === a.slug} onSelect={pick} />
                    {selected?.slug === a.slug && openSoon && (
                      <div className="bg-background px-6 pb-6">
                        <NotifyMeForm advisor={a} />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {!rosterLoading && advisors.length > 0 && (
              <p className="meta-mono mt-6 !text-white/45" data-testid="ua-price-note">
                <span className="text-primary">*</span> All prices exclusive of 18% GST. Full breakdown shown at checkout.
              </p>
            )}
          </div>

          <aside className="lg:col-span-5" data-testid="ua-booking-panel">
            <div className="sticky top-24 border border-border p-6 md:p-8 max-h-[calc(100vh-7rem)] overflow-y-auto scroll-thin">
              {confirmed ? (
                <div data-testid="booking-success">
                  <p className="label-mono !text-secondary mb-4">✓ CONFIRMED</p>
                  <h3 className="font-display text-3xl font-bold leading-tight">You&apos;re on with <span className="hl-coral">{confirmed.advisor_name}.</span></h3>
                  <div className="mt-6 border-t border-b border-border py-5">
                    <p className="meta-mono">— YOUR ARTIZEN ID</p>
                    <p className="num-display text-3xl md:text-4xl font-bold mt-2 tracking-tight text-primary" data-testid="booking-artizen-id">{confirmed.artizen_id}</p>
                    <p className="meta-mono mt-3 !text-white/55">— PERMANENT · YOURS FOREVER · USE IT FOR REPEAT BOOKINGS</p>
                  </div>
                  <p className="mt-5 text-white/80 text-sm leading-relaxed">Calendar invite + Zoom link will hit <strong>{user?.email}</strong> within 24 hours. Book again anytime from your dashboard.</p>
                  <button onClick={() => setConfirmed(null)} className="btn-pill mt-6" data-testid="booking-another">Book another session</button>
                </div>
              ) : selected?.has_slots === false ? (
                <div data-testid="booking-opening-soon">
                  <p className="label-mono !text-primary mb-4">— OPENING SOON</p>
                  <h3 className="font-display text-3xl font-bold leading-tight">
                    <span className="hl-coral">{selected.name}</span>&apos;s calendar is <br />full right now.
                  </h3>
                  <p className="mt-5 text-white/75 text-sm leading-relaxed">
                    Pick another advisor from the roster, or drop your email below and we&apos;ll ping you the moment {selected.name.split(" ")[0]} opens new slots.
                  </p>
                  <div className="mt-5">
                    <NotifyMeForm advisor={selected} />
                  </div>
                </div>
              ) : reviewMode && user && selected && slot ? (
                <div data-testid="booking-review">
                  <p className="label-mono !text-secondary mb-4">— READY TO CONFIRM</p>
                  <h3 className="font-display text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight">
                    30 minutes with <span className="hl-coral">{selected.name}.</span>
                  </h3>
                  <p className="meta-mono !text-primary mt-3">{selected.expertise || selected.role}</p>

                  <div className="mt-6 border-t border-border pt-5">
                    <p className="meta-mono !text-white/55">— WHEN</p>
                    <p className="font-mono text-sm mt-2" data-testid="review-slot">
                      {(() => {
                        const d = new Date(slot);
                        const ist = `${d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })} · ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" })} IST`;
                        return ist;
                      })()}
                    </p>
                    <p className="font-mono text-xs mt-1 text-white/55" data-testid="review-slot-local">
                      Your time: {localTzLabel(slot)}
                    </p>
                  </div>

                  <div className="mt-5 border-t border-border pt-5">
                    <p className="meta-mono !text-white/55">— WHY {selected.name.split(" ")[0].toUpperCase()}</p>
                    <p className="text-sm text-white/85 mt-2 leading-relaxed">{whyAdvisor}</p>
                  </div>
                  <div className="mt-5 border-t border-border pt-5">
                    <p className="meta-mono !text-white/55">— WHAT YOU WANT TO TALK ABOUT</p>
                    <p className="text-sm text-white/85 mt-2 leading-relaxed">{whatToDiscuss}</p>
                  </div>

                  {(bookName || bookPhone || resolvedDiscipline || resolvedCity) && (
                    <div className="mt-5 border-t border-border pt-5" data-testid="review-artizen-id">
                      <p className="meta-mono !text-white/55">— YOUR ARTIZEN ID (issues on confirm)</p>
                      <p className="font-mono text-sm mt-2 text-primary">
                        ANZ-{"0".repeat(4)}-{(resolvedDiscipline || "art").slice(0, 3).toUpperCase()}-{(resolvedCity || "ind").slice(0, 3).toUpperCase()}
                      </p>
                      <p className="text-xs text-white/50 mt-1">{bookName} · {bookPhone} · {resolvedDiscipline} · {resolvedCity}</p>
                    </div>
                  )}

                  <div className="mt-6 border border-border p-4 bg-black/20">
                    <p className="meta-mono mb-3 !text-white/55">— PRICE BREAKDOWN</p>
                    <div className="flex justify-between text-sm text-white/75 py-1"><span>Session fee</span><span className="font-mono">₹{base.toLocaleString("en-IN")}</span></div>
                    {appliedPromo && (
                      <div className="flex justify-between text-sm text-primary py-1" data-testid="review-discount-line">
                        <span>{appliedPromo.code} · {appliedPromo.discount_pct}% off</span>
                        <span className="font-mono">-₹{discount.toLocaleString("en-IN")}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-white/75 py-1"><span>GST (18%)</span><span className="font-mono">₹{gst.toLocaleString("en-IN")}</span></div>
                    <div className="flex justify-between text-base font-bold text-white pt-2 mt-2 border-t border-border"><span>Total</span><span className="font-mono">₹{total.toLocaleString("en-IN")}</span></div>
                  </div>

                  <button
                    data-testid="booking-review-confirm"
                    onClick={book}
                    disabled={booking}
                    className="btn-pill btn-coral w-full justify-center mt-6 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {booking ? "Opening checkout…" : <><Clock size={14} /> Book Now · ₹{total.toLocaleString("en-IN")} <ArrowRight size={14} /></>}
                  </button>
                  <button
                    data-testid="booking-review-edit"
                    onClick={() => setReviewMode(false)}
                    className="w-full mt-3 text-center meta-mono !text-white/55 hover:!text-white transition"
                  >
                    — EDIT DETAILS
                  </button>
                  <p className="meta-mono mt-4 !text-white/45 text-center">— SECURE PAYMENT · RAZORPAY</p>
                </div>
              ) : (
                <>
                  <p className="label-mono mb-6">Booking · 30 min · ₹{total.toLocaleString("en-IN")} <span className="!text-white/50 font-normal">(inc. GST)</span></p>
                  {!selected ? (
                    <p className="font-display text-3xl font-bold leading-tight">Pick an advisor to see <span className="hl-cyan">availability.</span></p>
                  ) : (
                    <>
                      <h3 className="font-display text-3xl font-bold leading-tight">{selected.name}</h3>
                      <p className="meta-mono !text-primary mt-2">{selected.expertise || selected.role}</p>

                      <div className="mt-6 border-t border-border pt-6">
                        <p className="meta-mono mb-4"><Calendar size={11} className="inline mr-1" /> Next 3 weeks · IST (local time shown below)</p>
                        {slotData.loading ? (
                          <p className="meta-mono !text-white/50">— LOADING SLOTS…</p>
                        ) : !slotData.configured ? (
                          <div className="border border-border bg-background p-5" data-testid="slots-coming-soon">
                            <p className="meta-mono !text-primary mb-2">— SLOTS COMING SOON</p>
                            <p className="text-sm text-white/70 leading-relaxed">
                              {selected.name.split(" ")[0]} hasn&apos;t opened their calendar yet. Check back in a couple of days, or drop us a note at <a href="mailto:hello@anartizen.com" className="text-primary underline">hello@anartizen.com</a> and we&apos;ll ping you when they do.
                            </p>
                          </div>
                        ) : slotData.slots.length === 0 ? (
                          <div className="border border-border bg-background p-5" data-testid="slots-none-left">
                            <p className="meta-mono !text-primary mb-2">— FULLY BOOKED</p>
                            <p className="text-sm text-white/70 leading-relaxed">All open slots for the next 3 weeks are taken. Try again next week or reach out at <a href="mailto:hello@anartizen.com" className="text-primary underline">hello@anartizen.com</a>.</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[260px] overflow-auto scroll-thin">
                            {slotData.slots.map((s) => {
                              const d = new Date(s);
                              const istLabel = `${d.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" })} · ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" })} IST`;
                              const local = localTzLabel(s);
                              return (
                                <button
                                  key={s}
                                  data-testid={`slot-${s}`}
                                  onClick={() => { setSlot(s); trackEvent("ua_slot_selected", { advisor_slug: selected.slug, slot_iso: s }); }}
                                  className={`p-2 border text-[10px] font-mono uppercase tracking-wider text-left ${slot === s ? "border-primary bg-primary text-white" : "border-border hover:border-white text-white/80"}`}
                                >
                                  <span className="block">{istLabel}</span>
                                  <span className={`block mt-0.5 ${slot === s ? "text-white/80" : "text-white/45"}`}>{local}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Artizen ID · profile capture */}
                      <div className="mt-6 border-t border-border pt-6" data-testid="booking-profile-block">
                        <p className="meta-mono mb-1">— YOUR ARTIZEN ID</p>
                        <p className="text-xs text-white/50 mb-4">These four details mint your permanent ID — e.g. <span className="font-mono text-white/70">ANZ-0042-MUS-MUM</span>. Used for every future booking.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                          <div>
                            <label className="meta-mono !text-white/55 block mb-1">— NAME</label>
                            <input
                              data-testid="booking-name"
                              value={bookName}
                              onChange={(e) => setBookName(e.target.value)}
                              maxLength={120}
                              placeholder="Your full name"
                              className="w-full bg-transparent outline-none text-sm border-b border-border focus:border-primary py-2"
                            />
                          </div>
                          <div>
                            <label className="meta-mono !text-white/55 block mb-1">— PHONE</label>
                            <input
                              data-testid="booking-phone"
                              type="tel"
                              inputMode="tel"
                              value={bookPhone}
                              onChange={(e) => setBookPhone(e.target.value)}
                              maxLength={20}
                              placeholder="+91 98… or +1 415…"
                              className="w-full bg-transparent outline-none text-sm border-b border-border focus:border-primary py-2"
                            />
                            <p className="text-[10px] text-white/40 mt-1">International formats welcome (+country code).</p>
                          </div>
                          <div>
                            <label className="meta-mono !text-white/55 block mb-1">— DISCIPLINE</label>
                            <select
                              data-testid="booking-discipline"
                              value={bookDiscipline}
                              onChange={(e) => { setBookDiscipline(e.target.value); if (e.target.value !== "other") setBookDisciplineOther(""); }}
                              className="w-full bg-transparent outline-none text-sm border-b border-border focus:border-primary py-2 appearance-none cursor-pointer"
                            >
                              <option value="" className="bg-background">Pick one…</option>
                              {DISCIPLINES.map((d) => (
                                <option key={d.key} value={d.key} className="bg-background">{d.label}</option>
                              ))}
                              {EXTRA_DISCIPLINES.map((d) => (
                                <option key={d.key} value={d.key} className="bg-background">{d.label}</option>
                              ))}
                            </select>
                            {bookDiscipline === "other" && (
                              <input
                                data-testid="booking-discipline-other"
                                value={bookDisciplineOther}
                                onChange={(e) => setBookDisciplineOther(e.target.value)}
                                maxLength={40}
                                placeholder="Tell us in one word (e.g. jewellery)"
                                className="w-full mt-2 bg-transparent outline-none text-sm border-b border-border focus:border-primary py-2"
                              />
                            )}
                          </div>
                          <div>
                            <label className="meta-mono !text-white/55 block mb-1">— CITY</label>
                            <select
                              data-testid="booking-city"
                              value={bookCity}
                              onChange={(e) => { setBookCity(e.target.value); if (e.target.value !== "other_india" && e.target.value !== "outside_india") setBookCityOther(""); }}
                              className="w-full bg-transparent outline-none text-sm border-b border-border focus:border-primary py-2 appearance-none cursor-pointer"
                            >
                              <option value="" className="bg-background">Pick one…</option>
                              {CITIES.map((c) => (
                                <option key={c} value={c} className="bg-background capitalize">
                                  {CITY_LABEL[c] || (c.charAt(0).toUpperCase() + c.slice(1))}
                                </option>
                              ))}
                            </select>
                            {(bookCity === "other_india" || bookCity === "outside_india") && (
                              <input
                                data-testid="booking-city-other"
                                value={bookCityOther}
                                onChange={(e) => setBookCityOther(e.target.value)}
                                maxLength={60}
                                placeholder={bookCity === "outside_india" ? "City, Country (e.g. London, UK)" : "Which city in India?"}
                                className="w-full mt-2 bg-transparent outline-none text-sm border-b border-border focus:border-primary py-2"
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Two-question intake */}
                      <div className="mt-6">
                        <p className="meta-mono mb-2">— WHY THIS ADVISOR?</p>
                        <p className="text-xs text-white/50 mb-2">What drew you to book <strong>{selected.name}</strong> specifically?</p>
                        <textarea
                          data-testid="booking-why-advisor"
                          value={whyAdvisor}
                          onChange={(e) => setWhyAdvisor(e.target.value)}
                          rows={3}
                          maxLength={2000}
                          placeholder={`I want to talk to ${selected.name.split(" ")[0]} because…`}
                          className="w-full bg-transparent outline-none text-sm border-b border-border focus:border-primary py-2 resize-none"
                        />
                      </div>
                      <div className="mt-5">
                        <p className="meta-mono mb-2">— WHAT DO YOU WANT TO TALK ABOUT?</p>
                        <p className="text-xs text-white/50 mb-2">The one thing you&apos;d walk away with a clearer answer to.</p>
                        <textarea
                          data-testid="booking-what-discuss"
                          value={whatToDiscuss}
                          onChange={(e) => setWhatToDiscuss(e.target.value)}
                          rows={3}
                          maxLength={2000}
                          placeholder="I want to walk away with clarity on…"
                          className="w-full bg-transparent outline-none text-sm border-b border-border focus:border-primary py-2 resize-none"
                        />
                      </div>

                      {/* COUPON CODE · optional, validated server-side */}
                      <div className="mt-6 border-t border-border pt-6" data-testid="booking-coupon-block">
                        <p className="meta-mono mb-2">— COUPON CODE <span className="!text-white/40 normal-case tracking-normal">(optional)</span></p>
                        {appliedPromo ? (
                          <div className="flex items-center justify-between gap-3 border border-primary/50 bg-primary/[0.06] p-3" data-testid="coupon-applied">
                            <div>
                              <p className="font-mono text-sm text-primary"><strong>{appliedPromo.code}</strong> &middot; {appliedPromo.discount_pct}% off applied</p>
                              <p className="text-xs text-white/55 mt-0.5">You save ₹{discount.toLocaleString("en-IN")} on the session fee.</p>
                            </div>
                            <button
                              type="button"
                              onClick={removeCoupon}
                              data-testid="coupon-remove"
                              className="meta-mono !text-white/60 hover:!text-primary"
                            >— REMOVE</button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-stretch gap-2">
                              <input
                                data-testid="coupon-input"
                                value={couponInput}
                                onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); if (couponStatus.error) setCouponStatus({ loading: false, error: "" }); }}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                                maxLength={40}
                                placeholder="Have a code? Paste it here."
                                className="flex-1 bg-transparent outline-none text-sm border border-border focus:border-primary px-3 py-2 font-mono uppercase tracking-wider"
                              />
                              <button
                                type="button"
                                onClick={applyCoupon}
                                disabled={couponStatus.loading || !couponInput.trim()}
                                data-testid="coupon-apply"
                                className="btn-pill shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                              >{couponStatus.loading ? "Checking…" : "Apply"}</button>
                            </div>
                            {couponStatus.error && (
                              <p className="text-xs text-primary mt-2" data-testid="coupon-error">{couponStatus.error}</p>
                            )}
                          </>
                        )}
                      </div>

                      <button data-testid="booking-confirm" onClick={book} disabled={booking || !slot} className="btn-pill btn-coral w-full justify-center mt-6 disabled:opacity-40 disabled:cursor-not-allowed">
                        {booking ? "Opening checkout…" : !slot ? <><Clock size={14} /> Pick a slot to continue</> : <><Clock size={14} /> {user ? "Pay & confirm" : "Sign in & continue"} · ₹{total.toLocaleString("en-IN")} <ArrowRight size={14} /></>}
                      </button>

                      {/* Price breakdown — visible only when a slot is picked */}
                      {slot && (
                        <div className="mt-4 border border-border p-4 bg-black/20" data-testid="booking-breakdown">
                          <p className="meta-mono mb-3 !text-white/55">— PRICE BREAKDOWN</p>
                          <div className="flex justify-between text-sm text-white/75 py-1">
                            <span>Session fee</span>
                            <span className="font-mono" data-testid="breakdown-base">₹{base.toLocaleString("en-IN")}</span>
                          </div>
                          {appliedPromo && (
                            <div className="flex justify-between text-sm text-primary py-1" data-testid="breakdown-discount">
                              <span>{appliedPromo.code} · {appliedPromo.discount_pct}% off</span>
                              <span className="font-mono">-₹{discount.toLocaleString("en-IN")}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm text-white/75 py-1">
                            <span>GST (18%)</span>
                            <span className="font-mono" data-testid="breakdown-gst">₹{gst.toLocaleString("en-IN")}</span>
                          </div>
                          <div className="flex justify-between text-base font-bold text-white pt-2 mt-2 border-t border-border">
                            <span>Total</span>
                            <span className="font-mono" data-testid="breakdown-total">₹{total.toLocaleString("en-IN")}</span>
                          </div>
                        </div>
                      )}
                      {!user && <p className="meta-mono mt-4 text-center !text-primary">— GOOGLE SIGN-IN · TAKES 2 SECONDS · YOUR ARTIZEN ID IS ISSUED ON CONFIRM</p>}
                      <p className="meta-mono mt-4 !text-white/45 text-center">— SECURE PAYMENT · RAZORPAY · CANCEL ANYTIME BEFORE PAYING</p>
                    </>
                  )}
                </>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ — rendered + emitted as schema.org JSON-LD above for SEO. */}
      <section className="border-t border-border" data-testid="ua-faq">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-20">
          <SectionLabel>FAQ · The common questions</SectionLabel>
          <h2 className="font-display text-4xl md:text-6xl font-bold leading-[0.95] tracking-tighter mb-10">
            Everything you&apos;d <span className="hl-cyan">ask before booking.</span>
          </h2>
          <div className="divide-y divide-border border-t border-b border-border">
            {FAQ.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`faq-item-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="font-display text-xl md:text-2xl font-bold leading-tight pr-6 group-open:text-primary transition-colors">
                    {f.q}
                  </h3>
                  <span className="meta-mono !text-primary shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-white/80 text-sm md:text-base leading-relaxed max-w-[720px]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* TERMS · IF THINGS SHIFT */}
      <section className="border-t border-border" data-testid="ua-terms">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
          <p className="label-mono mb-4">— TERMS · IF THINGS SHIFT</p>
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-bold leading-tight tracking-tight mb-3">
                We work in a <span className="hl-cyan">fluid industry.</span>
              </h2>
              <p className="text-white/75 text-sm leading-relaxed">
                We do our best to hold every session, but an advisor may occasionally need to move things around. If that happens, we&apos;ll notify you within 24 hours and offer the next available slot at no extra cost. Bear with us — the people you&apos;re booking are working artists first.
              </p>
            </div>
            <div>
              <h3 className="meta-mono !text-primary mb-2">— RESCHEDULE</h3>
              <p className="text-white/80 text-sm leading-relaxed">
                <strong>One free reschedule</strong> per booking, from your dashboard. Further changes are handled case-by-case — email <a href="mailto:hello@anartizen.com" className="text-primary underline">hello@anartizen.com</a> and we&apos;ll make it work.
              </p>
            </div>
            <div>
              <h3 className="meta-mono !text-primary mb-2">— CANCELLATION</h3>
              <p className="text-white/80 text-sm leading-relaxed">
                Sessions are <strong>non-refundable</strong>. You can cancel to free the slot for another member, but the fee is not returned. We recommend rescheduling instead. Full terms: <a href="/refunds" className="text-primary underline">Refunds policy</a>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
